import * as Sentry from "@sentry/vue";

export type FlowAttributeValue = string | number | boolean | object;
export type FlowAttributes = Record<string, FlowAttributeValue | FlowAttributeValue[] | null | undefined>;

export type FlowOutcome = "success" | "validation_error" | "api_error" | "abandoned";

const MAX_ATTRIBUTE_CHARS = 4000;

export const normalizeAttributes = (attributes: FlowAttributes): Record<string, FlowAttributeValue> => {
    const normalized: Record<string, FlowAttributeValue> = {};

    Object.entries(attributes).forEach(([key, value]) => {
        if (value === null || value === undefined || value === "") {
            return;
        }

        normalized[key] = typeof value === "string" && value.length > MAX_ATTRIBUTE_CHARS
            ? `${value.slice(0, MAX_ATTRIBUTE_CHARS)}... (truncated)`
            : value;
    });

    return normalized;
};

export const describeError = (error: any): FlowAttributes => {
    const details = Array.isArray(error?.response?.data?.errors)
        ? error.response.data.errors.map((err: any) => err?.detail).filter(Boolean)
        : [];

    return {
        "error": details.length > 0 ? details.join(" | ") : error?.message,
        "error.type": error?.code ?? error?.name ?? "Error",
        "error.stack": error?.stack,
        "http.status": error?.response?.status,
        "http.method": error?.config?.method?.toUpperCase(),
        "http.url": error?.config?.url,
    };
};

const HEX = "0123456789abcdef";

const randomHex = (length: number): string => {
    const bytes = new Uint8Array(length);
    if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
        crypto.getRandomValues(bytes);
    } else {
        for (let i = 0; i < length; i++) {
            bytes[i] = Math.floor(Math.random() * 16);
        }
    }
    return Array.from(bytes, byte => HEX[byte % 16]).join("");
};

export class Flow {
    public currentStep: string | null = null;
    public seq: number;

    public constructor(public readonly name: string, public readonly id: string = randomHex(32), seq: number = 0) {
        this.seq = seq;
    }

    public identity(): FlowAttributes {
        return {"flow.name": this.name, "flow.id": this.id};
    }

    private log(level: "info" | "warn" | "error", step: string, attributes: FlowAttributes): void {
        this.currentStep = step;
        this.seq += 1;
        Sentry.setTag("flow.step", step);

        if (activeFlow === this) {
            storeFlow(this);
        }

        Sentry.getIsolationScope().setAttribute("flow.step", step);
        Sentry.addBreadcrumb({
            category: this.name,
            message: step,
            level: level === "warn" ? "warning" : level,
            data: normalizeAttributes(attributes),
        });
        Sentry.logger[level](`${this.name}.${step}`, normalizeAttributes({
            ...attributes,
            ...this.identity(),
            "flow.step": step,
            "flow.seq": String(this.seq),
        }));
    }

    public info(step: string, attributes: FlowAttributes = {}): void {
        this.log("info", step, attributes);
    }

    public warn(step: string, attributes: FlowAttributes = {}): void {
        this.log("warn", step, attributes);
    }

    public fail(step: string, openIssue: boolean, error: any = null, attributes: FlowAttributes = {}): void {
        const details = error === null ? attributes : {...describeError(error), ...attributes};

        this.log("error", step, details);

        if (! openIssue) {
            return;
        }

        Sentry.captureException(error instanceof Error ? error : new Error(`${this.name}.${step}`), {
            extra: normalizeAttributes({...details, ...this.identity()}),
        });
    }

    public finish(outcome: FlowOutcome, attributes: FlowAttributes = {}): void {
        this.log(outcome === "success" ? "info" : "warn", "finished", {...attributes, "flow.outcome": outcome});

        if (activeFlow === this) {
            activeFlow = null;
        }
        clearStoredFlow();

        const scope = Sentry.getIsolationScope();
        scope.removeAttribute("flow.name");
        scope.removeAttribute("flow.id");
        scope.removeAttribute("flow.step");

        Sentry.setContext("flow", null);
        Sentry.setTag("flow.name", undefined);
        Sentry.setTag("flow.step", undefined);
    }
}

let activeFlow: Flow | null = null;

export const currentFlow = (): Flow | null => activeFlow;
export const activeFlowName = (): string | null => activeFlow?.name ?? null;
export const activeFlowId = (): string | null => activeFlow?.id ?? null;
export const activeFlowStep = (): string | null => activeFlow?.currentStep ?? null;
export const nextRequestSeq = (): number => {
    if (activeFlow === null) {
        return 0;
    }

    activeFlow.seq += 1;
    storeFlow(activeFlow);

    return activeFlow.seq;
};

export const adoptSeq = (value: number): void => {
    if (activeFlow === null || !Number.isFinite(value) || value <= activeFlow.seq) {
        return;
    }

    activeFlow.seq = value;
    storeFlow(activeFlow);
};

export const flowInfo = (step: string, attributes: FlowAttributes = {}): void => activeFlow?.info(step, attributes);
export const flowWarn = (step: string, attributes: FlowAttributes = {}): void => activeFlow?.warn(step, attributes);
export const flowFail = (step: string, openIssue: boolean, error: any = null, attributes: FlowAttributes = {}): void => activeFlow?.fail(step, openIssue, error, attributes);
export const flowFinish = (outcome: FlowOutcome, attributes: FlowAttributes = {}): void => activeFlow?.finish(outcome, attributes);

export const flowTrace = <T>(step: string, op: string, callback: () => T, attributes: FlowAttributes = {}): T => {
    const flow = activeFlow;

    if (flow === null) {
        return callback();
    }

    return Sentry.startNewTrace(() => Sentry.startSpan({
        name: `${flow.name}.${step}`,
        op,
        attributes: normalizeAttributes({...attributes, ...flow.identity()}) as Record<string, string | number | boolean>,
    }, callback));
};

const STORAGE_KEY = "sentry.flow";

const storeFlow = (flow: Flow): void => {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({name: flow.name, id: flow.id, seq: flow.seq}));
    } catch {
    }
};

const readStoredFlow = (name: string): {id: string, seq: number} | null => {
    try {
        const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? "null");
        return stored?.name === name ? {id: stored.id, seq: Number(stored.seq) || 0} : null;
    } catch {
        return null;
    }
};

const clearStoredFlow = (): void => {
    try {
        sessionStorage.removeItem(STORAGE_KEY);
    } catch {
    }
};

const openFlow = (flow: Flow): Flow => {
    activeFlow = flow;
    storeFlow(flow);

    Sentry.getIsolationScope().setAttributes(flow.identity() as Record<string, string>);

    Sentry.setTag("flow.name", flow.name);
    Sentry.setContext("flow", {name: flow.name, id: flow.id});

    return flow;
};

export const startFlow = (name: string, attributes: FlowAttributes = {}): Flow => {
    const flow = openFlow(new Flow(name));
    flow.info("started", attributes);

    return flow;
};

export const resumeFlow = (name: string, attributes: FlowAttributes = {}): Flow | null => {
    const stored = readStoredFlow(name);
    if (stored === null) {
        return null;
    }

    const flow = openFlow(new Flow(name, stored.id, stored.seq));
    flow.info("resumed", attributes);

    return flow;
};

export const continueFlow = (name: string, attributes: FlowAttributes = {}): Flow =>
    resumeFlow(name, attributes) ?? startFlow(name, attributes);
